import React, { useState } from 'react';

import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { post_payout_held_resolve } from '../api';
import {
  OPS_ACTIONS,
  REASON_MAX_LEN,
  bad_utr,
  has_error,
  resolve_done_text,
  resolve_errors
} from './adapters';
import { ACTION_SENT, resolve_body } from './requests';
import useIdempotentPost from './use_idempotent_post';

/*
 * PROD-7828 W4 — settle one held bank line: count as sent (bank reference and date; the line's own are
 * used when left blank, except a HOLD_BAD_UTR reference, which must be typed), count as failed, or ignore; a reason always. A line that matched no payment
 * needs the payment ID it answers. Confirmed in the dialog before it is sent.
 * props: line (line_view), today, on_close(), on_done(text), on_stale(text), on_error(e, fallback).
 */
const FAILED_TEXT = 'The line may not have been resolved. Reload the held lines to check.';

const ResolveDialog = ({ line, today, on_close, on_done, on_stale, on_error }) => {
  const [form, setForm] = useState({
    action: '',
    reason: '',
    bank_reference: '',
    sent_on: '',
    payout_item_uuid: ''
  });
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const { busy, send } = useIdempotentPost(FAILED_TEXT);
  const errors = resolve_errors(line, form, today);
  const sent = form.action === ACTION_SENT;
  // The stored copy of a bad UTR is not offered as the blank default: the server needs it typed.
  const own_utr = bad_utr(line) ? '' : line.bank_reference;
  const needs_item = !line.payout_item_uuid && form.action && form.action !== 'IGNORE';
  const set = (k) => (e) => {
    setConfirming(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };
  const field_error = (k) =>
    touched && errors[k] ? (
      <span className="tp-neg" role="alert">
        {errors[k]}
      </span>
    ) : null;
  const action_label = (OPS_ACTIONS.find((a) => a.value === form.action) || {}).label || '';

  const submit = () => {
    const body = resolve_body({ ...form, line_id: line.line_id });
    send(body, () => post_payout_held_resolve(body)).then((r) => {
      if (r.ok) {
        on_done(resolve_done_text(r.data, form.action));
        return;
      }
      if (r.err.kind === 'auth') on_error(r.raw, FAILED_TEXT);
      if (r.err.kind === 'stale') on_stale(r.err.text);
      setError(r.err.text);
      setConfirming(false);
    });
  };

  return (
    <TpDialog
      title={`Held line ${line.line_no} · ${line.amount}`}
      on_close={on_close}
      busy={busy}
      guard_text={discard_guard_text(form.reason)}
    >
      <div className="tp-modal tp-po-resolve">
        <div className="tp-ctx">
          <StatusPill tone={line.proposed.tone}>{line.proposed.label}</StatusPill>{' '}
          {line.beneficiary_name}
          {line.bank_reason ? ` · bank: ${line.bank_reason}` : ''}
          {line.teacher_name ? ` · ${line.teacher_name} ${line.pay_month}` : ''}
        </div>
        <fieldset className="tp-po-radios">
          <legend className="tp-hint">What should happen to this line?</legend>
          {OPS_ACTIONS.map((a) => (
            <label key={a.value} htmlFor={`tp-po-act-${a.value}`}>
              <input
                id={`tp-po-act-${a.value}`}
                type="radio"
                name="tp-po-action"
                value={a.value}
                checked={form.action === a.value}
                onChange={set('action')}
                disabled={busy}
              />{' '}
              {a.label}
            </label>
          ))}
        </fieldset>
        {field_error('action')}
        {sent ? (
          <div className="tp-field-2">
            <div className="tp-field">
              <label htmlFor="tp-po-res-utr">
                Bank reference (UTR){own_utr ? `, blank = ${own_utr}` : ''}
              </label>
              <input
                id="tp-po-res-utr"
                className="tp-input tp-mono"
                value={form.bank_reference}
                onChange={set('bank_reference')}
                maxLength={64}
              />
              {bad_utr(line) ? (
                <span className="tp-hint">
                  The bank&apos;s reference{line.bank_reference ? ` (${line.bank_reference})` : ''}{' '}
                  is not valid: type the correct UTR.
                </span>
              ) : null}
              {field_error('bank_reference')}
            </div>
            <div className="tp-field">
              <label htmlFor="tp-po-res-date">
                Sent on{line.value_date ? `, blank = ${line.value_date}` : ''}
              </label>
              <input
                id="tp-po-res-date"
                className="tp-input"
                type="date"
                max={today}
                value={form.sent_on}
                onChange={set('sent_on')}
              />
              {field_error('sent_on')}
            </div>
          </div>
        ) : null}
        {needs_item ? (
          <div className="tp-field">
            <label htmlFor="tp-po-res-item">Payment ID this line answers</label>
            <input
              id="tp-po-res-item"
              className="tp-input tp-mono"
              value={form.payout_item_uuid}
              onChange={set('payout_item_uuid')}
            />
            {field_error('payout_item_uuid')}
          </div>
        ) : null}
        <div className="tp-field">
          <label htmlFor="tp-po-res-reason">Reason (recorded)</label>
          <textarea
            id="tp-po-res-reason"
            className="tp-textarea"
            value={form.reason}
            maxLength={REASON_MAX_LEN * 2}
            onChange={set('reason')}
          />
          {field_error('reason')}
        </div>
        {confirming ? (
          <div className="tp-banner tp-banner-warn" role="alert">
            {action_label} line {line.line_no} ({line.amount})? This cannot be undone here.
          </div>
        ) : null}
        {error ? (
          <div className="tp-neg" role="alert">
            {error}
          </div>
        ) : null}
        <div className="tp-modal-ft">
          <button type="button" className="tp-btn-ghost" onClick={busy ? undefined : on_close}>
            Cancel
          </button>
          {confirming ? (
            <button
              type="button"
              className="tp-btn"
              onClick={busy ? undefined : submit}
              aria-disabled={busy ? 'true' : undefined}
            >
              {busy ? 'Saving…' : `Yes, ${action_label.toLowerCase()}`}
            </button>
          ) : (
            <button
              type="button"
              className="tp-btn-out"
              onClick={() => {
                setTouched(true);
                if (!has_error(errors)) setConfirming(true);
              }}
            >
              Resolve…
            </button>
          )}
        </div>
      </div>
    </TpDialog>
  );
};

export default ResolveDialog;
